/**
 * CustomerPortal - Main Customer Portal component (Dark Theme)
 * 
 * This is the refactored version with dark theme styling matching /buchen page
 * 
 * Structure:
 * - /components/portal/PortalContext.jsx - Context providers
 * - /components/portal/AuthForms.jsx - Login, Register, Password Reset forms
 * - /components/portal/BookingCalendar.jsx - Service booking calendar
 * - /components/portal/MyAppointments.jsx - Appointment management
 * - /components/portal/MyPackages.jsx - Packages, subscriptions, invoices, shop
 * - /components/portal/CustomerProfile.jsx - Profile and payment methods
 */
import React, { useState, useEffect, useRef, Suspense, lazy, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, Clock, Package, Apple, LogIn, LogOut, UserPlus, FileText, Home, Activity, HelpCircle, ClipboardList, Wallet, Dumbbell, ArrowLeft, UserCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PaymentChoicePrompt from '@/components/portal/PaymentChoicePrompt';

// Import portal components
import StatusBanner from '@/components/StatusBanner';
import {
  CustomerAuthContext,
  PortalSettingsContext,
  useCustomerAuth,
  usePortalSettings,
  defaultSettings,
  API
} from '@/components/portal/PortalContext';

// Import prefetch utility for faster navigation
import { prefetchPortalData, clearPortalCache } from '@/utils/portalPrefetch';
import { portalAuthHeaderValue } from '@/utils/tokenUtils';

import {
  LoginForm,
  RegisterForm
} from '@/components/portal/AuthForms';

import PortalDashboard from '@/components/portal/PortalDashboard';
import PublicInstallAppBanner from '@/components/portal/PublicInstallAppBanner';
import PortalLanguageControl from '@/components/portal/PortalLanguageControl';
import OpenItems from '@/components/portal/OpenItems';
import { GiftPointsDialog } from '@/components/portal/GiftPointsDialog';
import StrongFitLogo from '@/components/StrongFitLogo';
import VersionBadge from '@/components/VersionBadge';
import NutritionOnboarding from '@/components/nutrition/NutritionOnboarding';
import PortalSnowEffect from '@/components/portal/PortalSnowEffect';

// Additional components
import ProfileCompletion from '@/components/portal/ProfileCompletion';
import PrescriptionUploadStep from '@/components/portal/PrescriptionUploadStep';
import PrescriptionStatusBanner from '@/components/portal/PrescriptionStatusBanner';
import WelcomeDisclaimer from '@/components/portal/WelcomeDisclaimer';
import EmailCaptureDialog, { needsEmailCapture } from '@/components/portal/EmailCaptureDialog';

// Re-export hooks for external usage
export { useCustomerAuth, usePortalSettings };

// Portal Theme Configuration (mutated at runtime from tenant portal settings)
const darkTheme = {
  bg: '#0a0a0a',
  cardBg: '#141310',
  cardBgHover: '#1c1a15',
  accent: '#c9a34e', // StrongFit Gold
  text: '#f2f2ee',
  textMuted: '#8a8a85',
  border: '#2a2822'
};

const DARK_PALETTE = { bg: '#0a0a0a', cardBg: '#141310', cardBgHover: '#1c1a15', text: '#f2f2ee', textMuted: '#8a8a85', border: '#2a2822' };
const LIGHT_PALETTE = { bg: '#f7faf9', cardBg: '#ffffff', cardBgHover: '#eef2f1', text: '#1f2d2c', textMuted: '#64748b', border: '#e2e8f0' };

// Recompute the shared theme object from tenant settings. Returns the active mode.
const applyPortalTheme = (s) => {
  const mode = s.portal_theme === 'light' ? 'light' : 'dark';
  const base = mode === 'light' ? LIGHT_PALETTE : DARK_PALETTE;
  darkTheme.bg = s.portal_bg_color || base.bg;
  darkTheme.cardBg = base.cardBg;
  darkTheme.cardBgHover = base.cardBgHover;
  darkTheme.text = s.text_primary || base.text;
  darkTheme.textMuted = s.text_secondary || base.textMuted;
  darkTheme.border = s.border_color || base.border;
  if (s.button_color || s.primary_color) darkTheme.accent = s.button_color || s.primary_color;
  return mode;
};

const PORTAL_TOUR_VERSION = 1;

const getTourStorageKey = (customerId) => {
  if (!customerId) return `portal_tour_completed_v${PORTAL_TOUR_VERSION}`;
  return `portal_tour_completed_${customerId}_v${PORTAL_TOUR_VERSION}`;
};

const getTourAnyCompletedKey = (customerId) => {
  if (!customerId) return 'portal_tour_completed';
  return `portal_tour_completed_${customerId}`;
};

const getTourMajorUpdatesOptInKey = (customerId) => {
  if (!customerId) return 'portal_tour_major_updates_opt_in';
  return `portal_tour_major_updates_opt_in_${customerId}`;
};

// Lazy-loaded portal sections for faster initial load
const BookingCalendar = lazy(() => import('@/components/portal/BookingCalendar'));
const MyAppointments = lazy(() => import('@/components/portal/MyAppointments'));
const MyPackages = lazy(() => import('@/components/portal/MyPackages'));
const CustomerProfile = lazy(() => import('@/components/portal/CustomerProfile'));
const ConsentForms = lazy(() => import('@/components/portal/ConsentForms'));
const CustomerInsuranceDocuments = lazy(() => import('@/components/portal/CustomerInsuranceDocuments'));
const CombinedFitnessHub = lazy(() => import('@/components/portal/CombinedFitnessHub'));
const GroupFitnessTab = lazy(() => import('@/components/portal/GroupFitnessTab'));
const NutritionHub = lazy(() => import('@/components/portal/NutritionHub'));
const PortalInsuranceReceipts = lazy(() => import('@/components/portal/PortalInsuranceReceipts'));
const PortalInvoices = lazy(() => import('@/components/portal/PortalInvoices'));
const FriendshipTreeCard = lazy(() => import('@/components/portal/FriendshipTreeCard'));

const PortalTabLoader = ({ label, testId }) => (
  <div className="p-6 flex items-center justify-center" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div
        className="animate-spin h-5 w-5 border-2 border-t-transparent rounded-full"
        style={{ borderColor: darkTheme.accent }}
        data-testid={`${testId}-spinner`}
      />
      <span className="text-sm" style={{ color: darkTheme.textMuted }} data-testid={`${testId}-label`}>
        {label}
      </span>
    </div>
  </div>
);

const PortalTourOverlay = ({
  steps,
  stepIndex,
  isOpen,
  onNext,
  onBack,
  onClose,
  showOnMajorUpdates,
  onMajorUpdatesPreferenceChange
}) => {
  const { t } = useTranslation();
  const [targetRect, setTargetRect] = useState(null);
  const step = steps[stepIndex];

  useEffect(() => {
    if (!isOpen || !step) {
      setTargetRect(null);
      return;
    }

    const updateRect = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
      } else {
        setTargetRect(null);
      }
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen, stepIndex, step]);

  if (!isOpen || !step) return null;

  const padding = 10;
  const tooltipWidth = 320;
  const gap = 16;

  const highlightStyle = targetRect
    ? {
      top: Math.max(targetRect.top - padding, 8),
      left: Math.max(targetRect.left - padding, 8),
      width: Math.min(targetRect.width + padding * 2, window.innerWidth - 16),
      height: targetRect.height + padding * 2
    }
    : {
      top: window.innerHeight / 2 - 40,
      left: window.innerWidth / 2 - 120,
      width: 240,
      height: 80
    };

  const placement = step.placement || 'bottom';
  // Estimated tooltip height — real measurement happens after first render, but
  // we need a pre-render guess to decide placement. 260 covers: header (step
  // count + close), 3-line content, optional toggle, and button row.
  const estimatedTooltipHeight = 260;
  const viewportMargin = 20;

  let tooltipTop = highlightStyle.top + highlightStyle.height + gap;
  let tooltipLeft = highlightStyle.left;
  let transform = 'translateY(0)';

  if (placement === 'top') {
    tooltipTop = highlightStyle.top - gap;
    transform = 'translateY(-100%)';
  }

  if (placement === 'left') {
    tooltipTop = highlightStyle.top;
    tooltipLeft = highlightStyle.left - gap;
    transform = 'translateX(-100%)';
  }

  if (placement === 'right') {
    tooltipTop = highlightStyle.top;
    tooltipLeft = highlightStyle.left + highlightStyle.width + gap;
    transform = 'translateX(0)';
  }

  tooltipLeft = Math.min(Math.max(16, tooltipLeft), window.innerWidth - tooltipWidth - 16);

  // Compute the ACTUAL rendered top/bottom (after transform is applied) and
  // flip placement if it would clip. Previously we compared tooltipTop against
  // viewport bounds BEFORE transform — which meant a top-placed tooltip with
  // translateY(-100%) could still render half off-screen when the target was
  // near the top of the viewport (e.g. the "Buchen"-Tab at Line 50px).
  const renderedTop = transform.includes('-100%') ? tooltipTop - estimatedTooltipHeight : tooltipTop;
  const renderedBottom = renderedTop + estimatedTooltipHeight;

  if (renderedBottom > window.innerHeight - viewportMargin) {
    // Overflow at the bottom → place on top of the target
    tooltipTop = highlightStyle.top - gap;
    transform = 'translateY(-100%)';
  } else if (renderedTop < viewportMargin) {
    // Overflow at the top → place below the target
    tooltipTop = highlightStyle.top + highlightStyle.height + gap;
    transform = 'translateY(0)';
  }

  return (
    <div className="fixed inset-0 z-[2000] pointer-events-none" data-testid="portal-tour-overlay">
      <div className="fixed inset-0 bg-black/60" />
      <div
        className="fixed rounded-2xl border border-[#c9a34e]/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"
        style={{
          top: highlightStyle.top,
          left: highlightStyle.left,
          width: highlightStyle.width,
          height: highlightStyle.height
        }}
      />
      <div
        className="fixed flex items-center justify-center rounded-full border border-[#c9a34e]/70 bg-[#c9a34e] text-black text-xs font-bold"
        style={{
          top: highlightStyle.top - 10,
          left: highlightStyle.left - 10,
          width: 22,
          height: 22
        }}
        data-testid="portal-tour-beacon"
      >
        !
      </div>
      <div
        className="fixed w-[320px] max-w-[90vw] rounded-2xl border border-white/10 bg-[#1f1f1f] p-4 text-white shadow-[0_18px_40px_rgba(0,0,0,0.55)] pointer-events-auto"
        style={{
          top: tooltipTop,
          left: tooltipLeft,
          transform
        }}
        data-testid="portal-tour-tooltip"
      >
        <div className="flex items-center justify-between text-xs text-white/70" data-testid="portal-tour-step-count">
          <span>{t('portal.tour.step')} {stepIndex + 1} / {steps.length}</span>
          <button
            className="text-white/60 hover:text-white"
            onClick={onClose}
            data-testid="portal-tour-close"
          >
            ✕
          </button>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-white/90" data-testid="portal-tour-content">
          {step.content}
        </p>
        <div className="mt-4 space-y-3">
          <label
            className="flex items-center gap-2 text-xs text-white/80 cursor-pointer"
            data-testid="portal-tour-major-updates-toggle-label"
          >
            <input
              type="checkbox"
              checked={showOnMajorUpdates}
              onChange={(event) => onMajorUpdatesPreferenceChange(event.target.checked)}
              className="h-4 w-4 rounded border-white/30 bg-transparent"
              data-testid="portal-tour-major-updates-toggle"
            />
            {t('portal.tour.show_major_updates')}
          </label>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              className="rounded-lg border border-white/25 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/15 hover:border-white/40 transition-all"
              onClick={onBack}
              disabled={stepIndex === 0}
              style={{ opacity: stepIndex === 0 ? 0.4 : 1 }}
              data-testid="portal-tour-back"
            >
              {t('portal.tour.back')}
            </button>
            <div className="flex gap-2">
              <button
                className="rounded-lg border border-white/25 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/15 hover:border-white/40 transition-all"
                onClick={onClose}
                data-testid="portal-tour-skip"
              >
                {t('portal.tour.end')}
              </button>
              <button
                className="rounded-lg bg-[#c9a34e] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#d8b02b] transition-all"
                onClick={onNext}
                data-testid="portal-tour-next"
              >
                {stepIndex === steps.length - 1 ? t('portal.tour.done') : t('portal.tour.next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================
// CUSTOMER PORTAL MAIN COMPONENT
// =============================================

const CustomerPortal = () => {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customerToken'));

  // Embed-Sprache (?lang=xx von www.strongfit.ch): setzt die UI-Sprache
  // vor dem Login; nach dem Login gewinnt preferred_language aus dem Profil.
  useEffect(() => {
    const raw = (new URLSearchParams(window.location.search).get('lang') || '')
      .toLowerCase()
      .split('-')[0];
    if (!raw) return;
    const lang = ['de', 'en', 'fr', 'it', 'es', 'pt', 'sk', 'ru'].includes(raw) ? raw : 'en';
    import('@/i18n')
      .then((m) => {
        const inst = m.default || m.i18n;
        if (inst && inst.language !== lang) {
          localStorage.setItem('mobile_app_language', lang);
          inst.changeLanguage(lang);
        }
      })
      .catch(() => {});
  }, []);

  // Bidirektionaler Sprach-Sync (06/2026): preferred_language aus dem
  // Kundenprofil ist die Quelle der Wahrheit — Portal übernimmt sie bei
  // Login/Refresh automatisch (App-Wahl wirkt so auch im Web und umgekehrt).
  useEffect(() => {
    const lang = customer?.preferred_language;
    if (!lang || !['de', 'en', 'fr', 'it', 'es', 'pt', 'sk', 'ru'].includes(lang)) return;
    import('@/i18n')
      .then((m) => {
        const inst = m.default || m.i18n;
        if (inst && inst.language !== lang) {
          localStorage.setItem('mobile_app_language', lang);
          inst.changeLanguage(lang);
        }
      })
      .catch(() => {});
  }, [customer?.preferred_language]);

  // Check URL for embed mode to set default tab
  // embed=true → Only booking (no navigation)
  // embed=full → Full portal with navigation (no header)
  const urlParams = new URLSearchParams(window.location.search);
  const embedParam = urlParams.get('embed');
  const isEmbedBookingOnly = embedParam === 'true';
  const isEmbedFull = embedParam === 'full';
  const isEmbedInitial = isEmbedBookingOnly || isEmbedFull;
  const [activeTab, setActiveTab] = useState(isEmbedBookingOnly ? 'booking' : 'dashboard');
  const [loading, setLoading] = useState(true);
  const [isEmbed, setIsEmbed] = useState(isEmbedInitial);
  const [isEmbedFullPortal, setIsEmbedFullPortal] = useState(isEmbedFull);
  const showNavigation = !isEmbed || isEmbedFullPortal;
  const [portalSettings, setPortalSettings] = useState(defaultSettings);
  const [resetToken, setResetToken] = useState(null);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [showRxUpload, setShowRxUpload] = useState(false);
  const [rxPrefill, setRxPrefill] = useState(null);
  const [uploadedRxId, setUploadedRxId] = useState(null);
  const [bookingPreselectServiceId, setBookingPreselectServiceId] = useState(null);
  // 06/2026: Erst NACH einem vollständigen /auth/me-Load (Hydration) darf der
  // „Profil vervollständigen"-Dialog automatisch geprüft werden. Direkt nach
  // dem Login liefert die Login-Antwort nur ein minimales customer-Objekt
  // (ohne phone/accepted_terms/profile_completed) → das würde den Dialog
  // fälschlich öffnen (z.B. im Buchungs-Embed). Bug: ?embed=true zeigte den
  // Dialog trotz vollständigem Profil.
  const [profileHydrated, setProfileHydrated] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [profileIncompleteRedirect, setProfileIncompleteRedirect] = useState(false);
  const [showWelcomeDisclaimer, setShowWelcomeDisclaimer] = useState(false);
  const [showNutritionOnboarding, setShowNutritionOnboarding] = useState(false);
  const [portalMode, setPortalMode] = useState('dark');
  const isHighContrast = portalMode === 'dark';
  const [tourCompleted, setTourCompleted] = useState(false);
  const [tourStateLoaded, setTourStateLoaded] = useState(false);
  const [showTourOnMajorUpdates, setShowTourOnMajorUpdates] = useState(false);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  
  // Seasonal effects state
  const [seasonalEffects, setSeasonalEffects] = useState({
    snow_active: false,
    snow_intensity: 'medium',
    snow_speed: 'medium'
  });
  
  // Ref to track if we've already tried to load customer (prevents race conditions)
  const hasTriedToLoadRef = useRef(false);
  const isLoggedInRef = useRef(false);

  // Apply dark theme to html element for dialogs
  useEffect(() => {
    // Check for embed mode via URL parameter
    const params = new URLSearchParams(window.location.search);
    const embedParam = params.get('embed');
    const isEmbedMode = embedParam === 'true' || embedParam === 'full';
    
    if (isEmbedMode || isEmbed) {
      // Transparent background for embed mode - seamless iframe integration
      document.documentElement.classList.add('embed-mode');
      document.documentElement.classList.toggle('dark', portalMode === 'dark');
      document.documentElement.style.backgroundColor = 'transparent';
      document.body.style.backgroundColor = 'transparent';
      document.documentElement.style.background = 'transparent';
      document.body.style.background = 'transparent';
      const root = document.getElementById('root');
      if (root) {
        root.style.backgroundColor = 'transparent';
        root.style.background = 'transparent';
      }
    } else {
      document.documentElement.classList.toggle('dark', portalMode === 'dark');
      document.body.style.backgroundColor = darkTheme.bg;
    }
    
    return () => {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.remove('embed-mode');
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isEmbed, portalMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!customer?.id) {
      const legacyValue = localStorage.getItem('portal_tour_completed') === 'true';
      const anyCompletedValue = localStorage.getItem(getTourAnyCompletedKey()) === 'true';
      const currentVersionValue = localStorage.getItem(getTourStorageKey()) === 'true';
      const majorUpdatesOptIn = localStorage.getItem(getTourMajorUpdatesOptInKey()) === 'true';

      const hasCompletedAny = legacyValue || anyCompletedValue || currentVersionValue;
      const shouldShowForMajorUpdate = hasCompletedAny && !currentVersionValue && majorUpdatesOptIn;

      setShowTourOnMajorUpdates(majorUpdatesOptIn);
      setTourCompleted(hasCompletedAny ? !shouldShowForMajorUpdate : false);
      setTourStateLoaded(true);
      return;
    }

    // Check DB for tour_completed status (persists across devices/browsers)
    const checkDbTourStatus = async () => {
      try {
        const customerToken = localStorage.getItem('customerToken');
        if (customerToken) {
          const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/tour-status`, {
            headers: { Authorization: `Bearer ${customerToken}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.tour_completed) {
              // Sync to localStorage and mark as completed
              localStorage.setItem(getTourStorageKey(customer.id), 'true');
              localStorage.setItem(getTourAnyCompletedKey(customer.id), 'true');
              localStorage.setItem('portal_tour_completed', 'true');
              setTourCompleted(true);
              setTourStateLoaded(true);
              return;
            }
          }
        }
      } catch (e) {
        // Fall through to localStorage check
      }

      // Fallback to localStorage
      const scopedKey = getTourStorageKey(customer.id);
      const scopedAnyCompletedKey = getTourAnyCompletedKey(customer.id);
      const scopedMajorUpdatesKey = getTourMajorUpdatesOptInKey(customer.id);

      const scopedCurrentVersionValue = localStorage.getItem(scopedKey) === 'true';
      const scopedAnyCompletedValue = localStorage.getItem(scopedAnyCompletedKey) === 'true';
      const legacyValue = localStorage.getItem('portal_tour_completed') === 'true';
      const majorUpdatesOptIn = localStorage.getItem(scopedMajorUpdatesKey) === 'true';

      const hasCompletedAny = scopedCurrentVersionValue || scopedAnyCompletedValue || legacyValue;
      const shouldShowForMajorUpdate = hasCompletedAny && !scopedCurrentVersionValue && majorUpdatesOptIn;
      const completed = hasCompletedAny ? !shouldShowForMajorUpdate : false;

      if (hasCompletedAny && !scopedAnyCompletedValue) {
        localStorage.setItem(scopedAnyCompletedKey, 'true');
      }
      if (legacyValue && !scopedCurrentVersionValue && !majorUpdatesOptIn) {
        localStorage.setItem(scopedKey, 'true');
      }

      setShowTourOnMajorUpdates(majorUpdatesOptIn);
      setTourCompleted(completed);
      setTourStateLoaded(true);
    };

    checkDbTourStatus();
  }, [customer?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !tourStateLoaded) return;

    if (customer?.id) {
      localStorage.setItem(
        getTourMajorUpdatesOptInKey(customer.id),
        showTourOnMajorUpdates ? 'true' : 'false'
      );
    } else {
      localStorage.setItem(
        getTourMajorUpdatesOptInKey(),
        showTourOnMajorUpdates ? 'true' : 'false'
      );
    }
  }, [showTourOnMajorUpdates, customer?.id, tourStateLoaded]);

  useEffect(() => {
    if (!tourStateLoaded || !customer || tourCompleted || isEmbedBookingOnly) return;
    const onboardingDone = localStorage.getItem('strongfit_onboarding_completed');
    if (!onboardingDone) return;
    const timer = setTimeout(() => {
      setIsTourRunning(true);
      setTourStepIndex(0);
    }, 800);
    return () => clearTimeout(timer);
  }, [tourStateLoaded, customer, tourCompleted, isEmbedBookingOnly]);

  // Handle Google OAuth return:
  // 1) Emergent auth via #session_id=
  // 2) Custom Google OAuth via /portal/google/callback route
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const processGoogleOAuthSession = async () => {
      // Check for session_id in URL hash (e.g., /portal#session_id=abc123)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          console.log('Processing Google OAuth session_id from URL hash');
          
          // Check if we're in a popup window
          const isPopup = window.opener && window.opener !== window;
          
          if (isPopup) {
            // We're in a popup - send message to parent and close
            console.log('In popup - sending session to parent window');
            window.opener.postMessage({
              type: 'oauth_session',
              session_id: sessionId
            }, '*');
            window.close();
            return;
          }
          
          // We're in the main window - process the session directly
          try {
            const response = await axios.post(`${API}/portal/auth/google/session`, {
              session_id: sessionId
            });
            
            // Clear the hash from URL
            window.history.replaceState(null, '', window.location.pathname);
            
            // Login the user - inline since handleLogin isn't defined yet
            const newToken = response.data.token;
            const customerData = response.data.customer;
            localStorage.setItem('customerToken', newToken);
            setToken(newToken);
            setCustomer(customerData);
            setActiveTab('dashboard');
            setLoading(false);
            // Vollständiges Profil nachladen (Login-Objekt ist minimal) →
            // verhindert falschen Profil-Dialog.
            try {
              const full = await axios.get(`${API}/portal/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` }
              });
              setCustomer((prev) => ({ ...(prev || {}), ...full.data }));
            } catch (e) { console.debug('google session hydrate failed:', e); }
            finally { setProfileHydrated(true); }
            
            // Prefetch common data in background for faster navigation
            prefetchPortalData(newToken).catch(err => 
              console.debug('Background prefetch completed:', err)
            );
            
            if (response.data.is_new_user) {
              console.log('New user created via Google');
            } else {
              console.log('Existing user logged in via Google');
            }
          } catch (error) {
            console.error('Google OAuth session processing failed:', error);
            // Clear the hash even on error
            window.history.replaceState(null, '', window.location.pathname);
            setLoading(false);
          }
        }
      }
    };
    
    processGoogleOAuthSession();
  }, []);

  // Check for reset-password URL
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    
    if (path.includes('/portal/reset-password') || params.get('token')) {
      const tokenFromUrl = params.get('token');
      if (tokenFromUrl) {
        setResetToken(tokenFromUrl);
        setActiveTab('reset-password');
      }
    }
  }, []);

  // Load portal settings and seasonal effects on mount (parallel)
  useEffect(() => {
    loadPortalBootstrap();
  }, []);

  const loadPortalBootstrap = async () => {
    const [settingsRes, effectsRes] = await Promise.allSettled([
      axios.get(`${API}/portal/settings/public`, { headers: { Authorization: portalAuthHeaderValue() } }),
      axios.get(`${API}/admin/mobile-app/settings/active-effects`)
    ]);

    if (settingsRes.status === 'fulfilled') {
      const s = { ...defaultSettings, ...settingsRes.value.data };
      const mode = applyPortalTheme(s);
      setPortalMode(mode);
      setPortalSettings(s);
    } else {
      console.error('Failed to load portal settings:', settingsRes.reason);
    }

    if (effectsRes.status === 'fulfilled') {
      setSeasonalEffects(effectsRes.value.data);
    } else {
      console.error('Failed to load seasonal effects:', effectsRes.reason);
    }
  };

  // Check if embedded in iframe or via URL parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const embedParam = params.get('embed');
    const isEmbedTrue = embedParam === 'true';
    const isEmbedFullParam = embedParam === 'full';
    
    try {
      // Set embed mode if embed=true, embed=full, or if in iframe
      const isInIframe = window.self !== window.top;
      setIsEmbed(isEmbedTrue || isEmbedFullParam || isInIframe);
      setIsEmbedFullPortal(isEmbedFullParam);
    } catch (e) {
      setIsEmbed(true);
    }
  }, []);

  // Load customer ONLY ONCE on mount from stored token
  useEffect(() => {
    // Prevent multiple calls
    if (hasTriedToLoadRef.current) {
      return;
    }
    hasTriedToLoadRef.current = true;
    
    const storedToken = localStorage.getItem('customerToken');
    if (storedToken && storedToken !== 'undefined' && storedToken !== 'null') {
      loadCustomer(storedToken);
    } else if (localStorage.getItem('refresh_token')) {
      // Kein Access-Token mehr, aber gültiger Refresh-Token → Session wiederherstellen
      refreshPortalSession().then((t) => {
        if (t) loadCustomer(t); else setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  // 07/2026: Session-Refresh — Kunde bleibt dank Refresh-Token (90 Tage)
  // eingeloggt, auch wenn der Access-Token (30 Min) abgelaufen ist.
  const refreshPortalSession = async () => {
    const rt = localStorage.getItem('refresh_token');
    if (!rt) return null;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) {
        if (res.status === 401) localStorage.removeItem('refresh_token');
        return null;
      }
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('customerToken', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        setToken(data.access_token);
        return data.access_token;
      }
    } catch { /* offline etc. */ }
    return null;
  };

  // Proaktiver Refresh alle 20 Minuten während eingeloggter Session
  useEffect(() => {
    if (!token) return;
    const iv = setInterval(() => { refreshPortalSession(); }, 20 * 60 * 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadCustomer = async (authToken) => {
    // Skip if already logged in via form
    if (isLoggedInRef.current) {
      setLoading(false);
      return;
    }
    
    if (!authToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/portal/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCustomer(response.data);
      setProfileHydrated(true);
      setToken(authToken);
      
      // Prefetch common data in background for faster navigation
      prefetchPortalData(authToken).catch(err => 
        console.debug('Background prefetch completed:', err)
      );
    } catch (error) {
      // Netzwerk-/Serverfehler (z.B. Deploy): Session NICHT löschen, später erneut versuchen
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) {
        setTimeout(() => loadCustomer(authToken), 8000);
        setLoading(false);
        return;
      }
      // Token abgelaufen → mit Refresh-Token still erneuern statt ausloggen
      const newToken = await refreshPortalSession();
      if (newToken) {
        try {
          const retry = await axios.get(`${API}/portal/auth/me`, {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          setCustomer(retry.data);
          setProfileHydrated(true);
          prefetchPortalData(newToken).catch(() => {});
          return;
        } catch { /* auch mit neuem Token fehlgeschlagen */ }
      }
      console.debug('Token expired, clearing session');
      // Token is invalid/expired - clear it
      localStorage.removeItem('customerToken');
      setToken(null);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  /// Re-fetch the logged-in customer from /portal/auth/me without affecting
  /// loading state. Used when admin-side changes (e.g. nutrition unlock,
  /// status change, package activation) should become visible in-portal
  /// without forcing the user to log out and back in.
  const refreshCustomer = async () => {
    const authToken = token || localStorage.getItem('customerToken');
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/portal/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCustomer(response.data);
      setProfileHydrated(true);
    } catch (err) {
      console.debug('refreshCustomer failed:', err);
    }
  };

  const handleLogin = (newToken, customerData, refreshToken = null) => {
    if (!newToken || !customerData) {
      console.error('[CustomerPortal] Missing token or customer data!');
      return;
    }
    
    // Mark as logged in to prevent /auth/me call
    isLoggedInRef.current = true;
    
    // Store token and set state
    localStorage.setItem('customerToken', newToken);
    // Iter 329g: Refresh-Token persistieren wenn vom Backend mitgegeben.
    // Customer-Portal nutzt denselben Key wie der Admin-Auth-Context, damit
    // der globale Axios-Interceptor (siehe AuthContext) auch hier greifen
    // kann sobald der AT mal abläuft.
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    setToken(newToken);
    setCustomer(customerData);
    setActiveTab('dashboard');
    setLoading(false);

    // 06/2026: Login-Antwort ist minimal → sofort vollständiges Profil laden,
    // damit profile_completed/phone/accepted_terms bekannt sind und der
    // „Profil vervollständigen"-Dialog NICHT fälschlich aufpoppt.
    (async () => {
      try {
        const full = await axios.get(`${API}/portal/auth/me`, {
          headers: { Authorization: `Bearer ${newToken}` }
        });
        setCustomer((prev) => ({ ...(prev || {}), ...full.data }));
      } catch (e) {
        console.debug('handleLogin profile hydrate failed:', e);
      } finally {
        setProfileHydrated(true);
      }
    })();

    // Sprachauswahl (First-Visit-Dialog / Umschalter) ins Profil übernehmen,
    // falls das Profil noch keine bevorzugte Sprache hat → geräteübergreifend
    // und für künftige E-Mail/WhatsApp-Kommunikation.
    try {
      const chosen = (localStorage.getItem('mobile_app_language') || '').split('-')[0];
      const SUP = ['de', 'en', 'fr', 'it', 'es', 'pt', 'sk', 'ru'];
      if (chosen && SUP.includes(chosen) && !customerData.preferred_language) {
        axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/auth/profile`,
          { preferred_language: chosen },
          { headers: { Authorization: `Bearer ${newToken}` } }
        ).catch(() => {});
      }
    } catch (_) { /* best-effort */ }
    
    // Prefetch common data in background for faster navigation
    prefetchPortalData(newToken).catch(err => 
      console.debug('Background prefetch completed with some skips:', err)
    );
  };

  // Phase 4: Nach dem Login mit dem bestehenden Konto die vorher (im
  // temporären Neukonto) hochgeladene Verordnung automatisch übernehmen.
  // Der OTP-Login hier ist der Identitätsnachweis für das Zielkonto.
  useEffect(() => {
    if (!token || !customer?.id) return;
    let pending = null;
    try { pending = localStorage.getItem('pending_rx_transfer'); } catch { pending = null; }
    if (!pending) return;
    try { localStorage.removeItem('pending_rx_transfer'); } catch { /* noop */ }
    (async () => {
      try {
        const r = await axios.post(
          `${API}/portal/prescriptions/claim-transfer`,
          { transfer_token: pending },
          { headers: { Authorization: portalAuthHeaderValue() } }
        );
        if (r.data?.transferred || r.data?.already) {
          toast.success('Deine Verordnung wurde deinem Konto hinzugefügt.');
          try {
            const s = await axios.get(
              `${API}/portal/booking/suggest-service`,
              { headers: { Authorization: portalAuthHeaderValue() } }
            );
            if (s.data?.service_id) setBookingPreselectServiceId(s.data.service_id);
          } catch { /* optional */ }
          setActiveTab('booking');
        }
      } catch {
        toast.error('Verordnung konnte nicht automatisch übertragen werden. Dein Physiotherapeut ordnet sie dir zu.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, customer?.id]);

  // 08/2026: Magic-Link-Empfang (?magic=TOKEN) — login-loser Zugang aus der
  // Einladung „Konto vervollständigen". Tauscht den Token gegen einen echten
  // Login-Token → danach öffnet die Profil-Vervollständigung automatisch.
  useEffect(() => {
    const magic = new URLSearchParams(window.location.search).get('magic');
    if (!magic) return;
    (async () => {
      try {
        setLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/auth/magic-exchange`,
          { token: magic }
        );
        const d = resp.data || {};
        if (d.token && d.customer) {
          handleLogin(d.token, d.customer, d.refresh_token);
        } else {
          toast.error('Link ungültig oder abgelaufen.');
          setLoading(false);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Link ungültig oder abgelaufen. Bitte neue Einladung anfordern.');
        setLoading(false);
      } finally {
        // Magic-Token aus der URL entfernen (nicht im Verlauf/teilbar lassen)
        const url = new URL(window.location.href);
        url.searchParams.delete('magic');
        window.history.replaceState({}, document.title, url.pathname + url.search);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  // Check if profile completion is needed after customer is set
  // 08/2026 FIX: Nicht mehr nur für Google-User — auch „Login via Mail"/OTP-
  // Konten, die inaktiv & unvollständig angelegt wurden (nur E-Mail), müssen
  // ihr Profil vervollständigen (Name/Telefon/Geburtsdatum + AGB) und werden
  // dadurch aktiviert — genau wie in der Mobile-App.
  const profileNeedsCompletion = (c) => {
    if (!c) return false;
    if (c.profile_completed) return false;
    // Inaktives Konto ODER es fehlen Pflichtangaben
    const missingCore = !c.first_name || !c.last_name || !c.phone ||
      (!c.birthdate && !c.date_of_birth) || !c.accepted_terms || !c.accepted_privacy;
    return c.status !== 'active' || missingCore;
  };

  // Direktlink aus E-Mail (?focus=emergency / ?complete=1) → Profil-Dialog
  // öffnen, damit der Kunde sofort z.B. den Notfallkontakt nachtragen kann.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if ((params.get('focus') === 'emergency' || params.get('complete') === '1') && customer) {
        setShowProfileCompletion(true);
      }
    } catch (err) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  useEffect(() => {
    try {
      // Nur prüfen, wenn das vollständige Profil geladen ist (Hydration) und
      // NICHT im reinen Buchungs-Embed (dort soll das Widget nur buchen, kein
      // Profil-Nag). Bug 06/2026: ?embed=true zeigte den Dialog fälschlich.
      if (customer && profileHydrated && !isEmbedBookingOnly && profileNeedsCompletion(customer) && !showProfileCompletion && !showRxUpload) {
        const wantsRxUpload = (() => {
          try { return localStorage.getItem('onboarding_has_prescription') === 'yes' && !rxPrefill; }
          catch { return false; }
        })();
        const timer = setTimeout(() => {
          if (wantsRxUpload) {
            setShowRxUpload(true);
          } else if (typeof setShowProfileCompletion === 'function') {
            setShowProfileCompletion(true);
          }
        }, 400);
        return () => clearTimeout(timer);
      }
    } catch (err) {
      console.error('Error checking profile completion:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, profileHydrated, isEmbedBookingOnly, customer?.first_name, customer?.last_name, customer?.phone, customer?.birthdate, customer?.date_of_birth, customer?.accepted_terms, customer?.accepted_privacy, customer?.profile_completed, customer?.status]);

  // E-Mail-Nacherfassung (08/2026): SMS-OTP-Konten haben eine Platzhalter-
  // Adresse — bei jedem Login (1x pro Session) nach der echten E-Mail fragen.
  useEffect(() => {
    if (!token || !customer) return;
    if (!needsEmailCapture(customer)) return;
    if (sessionStorage.getItem('email_capture_dismissed')) return;
    const timer = setTimeout(() => setShowEmailCapture(true), 1500);
    return () => clearTimeout(timer);
  }, [token, customer?.id, customer?.email]);

  // Check if legal acceptance is needed after login
  // Check if legal/disclaimer acceptance is needed (combines FirstTimeDisclaimer + LegalAcceptanceModal)
  useEffect(() => {
    const checkAcceptanceNeeded = async () => {
      if (!token || !customer) return;
      
      // First check: localStorage for quick local check
      const hasLocalAccepted = localStorage.getItem('terms_accepted');
      
      try {
        // Check server for legal acceptance requirements
        const response = await axios.get(`${API}/portal/auth/legal-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Show welcome disclaimer ONLY if server says acceptance is needed
        // This is the authoritative check - server knows if user has accepted
        // 08/2026: NICHT zeigen wenn ohnehin die Profil-Vervollständigung
        // fällig ist — diese enthält AGB/Datenschutz bereits und aktiviert.
        if (response.data.needs_acceptance && !profileNeedsCompletion(customer)) {
          console.log('Welcome/Legal acceptance required by server');
          setShowWelcomeDisclaimer(true);
        } else {
          // Server says user has accepted - update localStorage to avoid future checks
          if (!hasLocalAccepted) {
            localStorage.setItem('terms_accepted', Date.now().toString());
          }
        }
      } catch (error) {
        // If server check fails but no local acceptance, show dialog anyway
        if (!hasLocalAccepted) {
          setShowWelcomeDisclaimer(true);
        }
        console.error('Error checking acceptance:', error);
      }
    };
    
    checkAcceptanceNeeded();
  }, [token, customer?.id]);

  const handleLogout = async () => {
    // Iter 329g: Refresh-Token serverseitig revoken bevor wir local
    // alles clearen. Best-effort — bei Netzfehler trotzdem logout.
    const rt = localStorage.getItem('refresh_token');
    if (rt) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
          // 3s timeout damit Logout nicht hängt
          signal: AbortSignal.timeout?.(3000),
        });
      } catch (e) {
        /* idempotent — ignore */
      }
    }
    localStorage.removeItem('customerToken');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setCustomer(null);
    setActiveTab('booking');
    
    // Clear all cached portal data
    clearPortalCache();
  };

  const toggleContrastMode = () => {};

  const renderContrastToggle = () => null;

  const startTour = () => {
    if (!customer) {
      setActiveTab('login');
    } else if (activeTab !== 'dashboard') {
      setActiveTab('dashboard');
    }
    setIsTourRunning(true);
    setTourStepIndex(0);
  };

  const renderTourButton = (location = 'header') => (
    <Button
      variant="ghost"
      size="sm"
      onClick={startTour}
      className="gap-2 border border-white/10 hover:bg-white/10"
      style={{ color: darkTheme.textMuted }}
      data-testid={location === 'embed' ? 'portal-help-tour-button-embed' : 'portal-help-tour-button'}
    >
      <HelpCircle size={16} />
      <span className="hidden sm:inline">{t('portal.header.tour_start')}</span>
      <span className="sm:hidden">{t('portal.header.tour')}</span>
    </Button>
  );

  const tourSteps = useMemo(() => {
    const helpTarget = isEmbed ? '[data-testid="portal-help-tour-button-embed"]' : '[data-testid="portal-help-tour-button"]';
    const contrastTarget = isEmbed ? '[data-testid="portal-contrast-toggle-embed"]' : '[data-testid="portal-contrast-toggle-header"]';

    if (!customer) {
      return [
        {
          target: '[data-testid="portal-login-form"]',
          content: t('portal.tour.guest_login'),
          placement: 'bottom'
        },
        {
          target: '[data-testid="portal-login-google-button"]',
          content: t('portal.tour.guest_google'),
          placement: 'bottom'
        },
        {
          target: helpTarget,
          content: t('portal.tour.help'),
          placement: 'bottom'
        }
      ];
    }

    if (!showNavigation) {
      return [
        {
          target: helpTarget,
          content: t('portal.tour.help'),
          placement: 'bottom'
        }
      ];
    }

    return [
      {
        target: helpTarget,
        content: t('portal.tour.help'),
        placement: 'bottom'
      },
      {
        target: '[data-testid="portal-dashboard-welcome"]',
        content: t('portal.tour.welcome'),
        placement: 'bottom',
        meta: { tab: 'dashboard' }
      },
      {
        target: '[data-testid="portal-dashboard-quick-actions"]',
        content: t('portal.tour.quick_actions'),
        placement: 'top',
        meta: { tab: 'dashboard' }
      },
      {
        target: '[data-testid="portal-nav-booking-button"]',
        content: t('portal.tour.booking'),
        placement: 'top',
        meta: { tab: 'booking' }
      },
      {
        target: '[data-testid="portal-nav-appointments-button"]',
        content: t('portal.tour.appointments'),
        placement: 'top',
        meta: { tab: 'appointments' }
      },
      {
        target: '[data-testid="portal-nav-packages-button"]',
        content: t('portal.tour.packages'),
        placement: 'top',
        meta: { tab: 'packages' }
      },
      {
        target: '[data-testid="portal-nav-fitness-button"]',
        content: t('portal.tour.fitness'),
        placement: 'top',
        meta: { tab: 'fitness' }
      },
      {
        target: '[data-testid="portal-nav-nutrition-button"]',
        content: t('portal.tour.nutrition'),
        placement: 'top',
        meta: { tab: 'nutrition' }
      },
      {
        target: '[data-testid="portal-nav-documents-button"]',
        content: t('portal.tour.documents'),
        placement: 'top',
        meta: { tab: 'documents' }
      },
      {
        target: '[data-testid="portal-nav-profile-button"]',
        content: t('portal.tour.profile'),
        placement: 'top',
        meta: { tab: 'profile' }
      }
    ];
  }, [customer, showNavigation, isEmbed, t]);

  const closeTour = () => {
    if (typeof window !== 'undefined') {
      if (customer?.id) {
        localStorage.setItem(getTourAnyCompletedKey(customer.id), 'true');
        localStorage.setItem(getTourStorageKey(customer.id), 'true');
      } else {
        localStorage.setItem(getTourAnyCompletedKey(), 'true');
        localStorage.setItem(getTourStorageKey(), 'true');
      }
      localStorage.setItem('portal_tour_completed', 'true');

      // Persist to DB so it works across devices/browsers
      const customerToken = localStorage.getItem('customerToken');
      if (customerToken) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/tour-completed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${customerToken}` }
        }).catch(() => {});
      }
    }

    setIsTourRunning(false);
    setTourCompleted(true);
    setTourStepIndex(0);
  };

  const handleTourNext = () => {
    if (tourStepIndex >= tourSteps.length - 1) {
      closeTour();
      return;
    }
    setTourStepIndex(prev => prev + 1);
  };

  const handleTourBack = () => {
    setTourStepIndex(prev => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    if (!isTourRunning) return;
    const step = tourSteps[tourStepIndex];
    if (step?.meta?.tab && activeTab !== step.meta.tab) {
      setActiveTab(step.meta.tab);
    }
  }, [isTourRunning, tourStepIndex, tourSteps, activeTab]);

  useEffect(() => {
    if (!isTourRunning) return;
    const step = tourSteps[tourStepIndex];
    if (!step) return;
    const element = document.querySelector(step.target);
    if (!element) {
      const timer = setTimeout(() => {
        if (tourStepIndex >= tourSteps.length - 1) {
          closeTour();
        } else {
          setTourStepIndex(prev => prev + 1);
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isTourRunning, tourStepIndex, tourSteps]);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: darkTheme.bg }}
        data-testid="portal-loading-screen"
      >
        <div 
          className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full" 
          style={{ borderColor: darkTheme.accent }}
          data-testid="portal-loading-spinner"
        />
      </div>
    );
  }

  return (
    <CustomerAuthContext.Provider value={{ customer, token, handleLogin, handleLogout, refreshCustomer }}>
      <PortalSettingsContext.Provider value={portalSettings}>
        {/* Snow Effect - renders behind all content */}
        {seasonalEffects.snow_active && (
          <PortalSnowEffect 
            intensity={seasonalEffects.snow_intensity || 'medium'} 
            speed={seasonalEffects.snow_speed || 'medium'} 
            enabled={seasonalEffects.snow_active}
          />
        )}

        <PortalTourOverlay
          steps={tourSteps}
          stepIndex={tourStepIndex}
          isOpen={isTourRunning && tourSteps.length > 0}
          onNext={handleTourNext}
          onBack={handleTourBack}
          onClose={closeTour}
          showOnMajorUpdates={showTourOnMajorUpdates}
          onMajorUpdatesPreferenceChange={setShowTourOnMajorUpdates}
        />
        
        <div 
          className={`min-h-screen ${portalMode === 'dark' ? 'dark' : 'portal-light'} ${isHighContrast ? 'portal-contrast' : ''} ${isEmbed ? 'p-4 pt-8' : 'p-4 md:p-8'}`}
          style={{ backgroundColor: 'transparent' }}
        >
          <div className={`max-w-5xl mx-auto ${isEmbed ? 'space-y-4' : 'space-y-4'}`} style={{ position: 'relative', zIndex: 10, backgroundColor: 'transparent' }}>
            {isEmbed && (
              <div className="flex justify-end gap-2" data-testid="portal-contrast-toggle-embed-wrapper">
                {renderTourButton('embed')}
              </div>
            )}
            
            {/* Header - Hidden completely in embed mode */}
            {!isEmbed && (
              <header 
                className="rounded-xl p-4 flex items-center justify-between"
                style={{ backgroundColor: darkTheme.cardBg, borderColor: darkTheme.border, borderWidth: '1px' }}
              >
                <div className="flex items-center gap-3">
                  {/* Mandanten-Logo (Fallback: neutrales Plattform-Logo) */}
                  <StrongFitLogo
                    size="sm"
                    showGlow={false}
                    src={portalSettings.logo_url || '/assets/invictafit-logo.png'}
                    alt={portalSettings.studio_name || 'Logo'}
                    data-testid="portal-tenant-logo"
                  />
                  <div>
                    <h1 className="font-bold text-base sm:text-lg" style={{ color: darkTheme.text, fontFamily: "'Outfit', sans-serif" }}>{portalSettings.studio_name || 'Studio'}</h1>
                    <p className="text-[10px] uppercase tracking-[0.15em] hidden sm:block" style={{ color: darkTheme.textMuted }}>{t('portal.header.subtitle')}</p>
                  </div>
                </div>
              
                {customer ? (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-sm hidden sm:block" style={{ color: darkTheme.textMuted }}>
                      {customer.first_name} {customer.last_name}
                    </span>
                    {Number(customer.credit_balance) > 0 && (
                      <Badge
                        className="border-0 text-xs inline-flex items-center gap-1"
                        style={{ backgroundColor: 'rgba(34,197,94,0.18)', color: '#22c55e' }}
                        data-testid="portal-credit-badge"
                      >
                        <Wallet size={12} />
                        CHF {Number(customer.credit_balance).toFixed(2)}
                      </Badge>
                    )}
                    <Badge 
                      className="border-0 text-xs"
                      style={{ backgroundColor: `${darkTheme.accent}20`, color: darkTheme.accent }}
                      data-testid="portal-points-badge"
                    >
                      {customer.points || 0} {t('portal.header.points')}
                    </Badge>
                    <span className="hidden sm:inline-flex">{renderTourButton('header')}</span>
                    <PortalLanguageControl firstVisitModal={false} />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleLogout}
                      className="hover:bg-white/10"
                      style={{ color: darkTheme.textMuted }}
                      data-testid="portal-logout-button"
                    >
                      <LogOut size={18} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {renderTourButton('header')}
                    <Button 
                      size="sm" 
                      onClick={() => setActiveTab('login')}
                      className="gap-2"
                      style={{ backgroundColor: darkTheme.accent, color: '#000' }}
                      data-testid="portal-login-button"
                    >
                      <LogIn size={16} />
                      {t('portal.header.login')}
                    </Button>
                  </div>
                )}
              </header>
            )}

            {/* Status Banner for service issues (customer view) */}
            <StatusBanner variant="customer" />

            {/* Main Content */}
            {customer ? (
              <CustomerDashboard 
                activeTab={activeTab} 
                setActiveTab={setActiveTab} 
                isEmbed={isEmbed}
                isEmbedFullPortal={isEmbedFullPortal}
                darkTheme={darkTheme}
                onOpenNutritionOnboarding={() => setShowNutritionOnboarding(true)}
                onStartTour={startTour}
                profileIncompleteRedirect={profileIncompleteRedirect}
                setProfileIncompleteRedirect={setProfileIncompleteRedirect}
                bookingPreselectServiceId={bookingPreselectServiceId}
              />
            ) : (
              <GuestView 
                activeTab={activeTab} 
                setActiveTab={setActiveTab}
                isEmbed={isEmbed}
                darkTheme={darkTheme}
              />
            )}
          </div>

          {/* Version indicator (subtle, only on non-embed customer portal) */}
          {!isEmbed && (
            <div className="pt-4 pb-2 flex justify-center opacity-60">
              <VersionBadge theme="dark" />
            </div>
          )}
        </div>
        
        {/* Welcome & Legal Acceptance Modal - Combined Dialog */}
        <WelcomeDisclaimer
          open={showWelcomeDisclaimer}
          token={token}
          studioName={portalSettings?.studio_name || 'Studio'}
          onAccepted={() => setShowWelcomeDisclaimer(false)}
          onDecline={() => {
            // User declined - log them out
            handleLogout();
          }}
        />
        
        {/* Verordnung-Upload-Schritt (Onboarding, vor Profil) */}
        <PrescriptionUploadStep
          open={showRxUpload}
          onComplete={(prefill, _ex, rxId) => {
            setRxPrefill(prefill || {});
            setUploadedRxId(rxId || null);
            setShowRxUpload(false);
            setShowProfileCompletion(true);
          }}
          onSkip={() => {
            setShowRxUpload(false);
            setShowProfileCompletion(true);
          }}
        />

        {/* Profile Completion Modal */}
        <ProfileCompletion
          open={showProfileCompletion}
          onClose={() => setShowProfileCompletion(false)}
          customer={customer}
          token={token}
          prefill={rxPrefill}
          forceComplete={profileNeedsCompletion(customer)}
          onUseExistingAccount={async () => {
            // Phase 4: Kunde hat erkannt, dass er bereits ein Konto hat →
            // ausloggen und zurück zur Anmeldung (bestehende E-Mail).
            setShowProfileCompletion(false);
            setRxPrefill(null);
            // Hochgeladene Verordnung zur Übernahme ins bestehende Konto
            // vormerken. Der OTP-Login des Zielkontos ist der Identitätsnachweis.
            if (uploadedRxId) {
              try {
                const r = await axios.post(
                  `${API}/portal/prescriptions/${uploadedRxId}/prepare-transfer`,
                  {},
                  { headers: { Authorization: portalAuthHeaderValue() } }
                );
                if (r.data?.transfer_token) {
                  localStorage.setItem('pending_rx_transfer', r.data.transfer_token);
                }
              } catch { /* best-effort */ }
            }
            try {
              localStorage.setItem('onboarding_is_new', 'no');
              localStorage.removeItem('onboarding_has_prescription');
            } catch { /* noop */ }
            toast.info('Bitte melde dich mit deinem bestehenden Konto an.');
            handleLogout();
          }}
          onProfileUpdated={(updatedCustomer) => {
            setCustomer(updatedCustomer);
            // Phase 3: Nach dem Onboarding direkt zur Buchung – mit KI-Vorauswahl,
            // falls eine Verordnung hochgeladen wurde.
            let isOnboarding = false;
            try {
              isOnboarding = !!(localStorage.getItem('onboarding_is_new') || localStorage.getItem('onboarding_has_prescription'));
            } catch { isOnboarding = false; }
            if (!isOnboarding) return;
            const hadRx = (() => { try { return ['yes', 'done'].includes(localStorage.getItem('onboarding_has_prescription')); } catch { return false; } })();
            const go = (svcId) => {
              if (svcId) setBookingPreselectServiceId(svcId);
              setActiveTab('booking');
              try {
                localStorage.removeItem('onboarding_is_new');
                localStorage.removeItem('onboarding_has_prescription');
              } catch { /* noop */ }
            };
            if (hadRx) {
              axios.get(`${API}/portal/booking/suggest-service`, { headers: { Authorization: portalAuthHeaderValue() } })
                .then((r) => go(r.data?.service_id))
                .catch(() => go(null));
            } else {
              go(null);
            }
          }}
        />

        {/* E-Mail-Nacherfassung für SMS-OTP-Konten */}
        <EmailCaptureDialog
          open={showEmailCapture}
          token={token}
          onClose={() => {
            setShowEmailCapture(false);
            sessionStorage.setItem('email_capture_dismissed', '1');
          }}
          onSaved={(newEmail) => {
            setCustomer((prev) => (prev ? { ...prev, email: newEmail } : prev));
          }}
        />
        
        {/* Nutrition Onboarding Modal */}
        <NutritionOnboarding
          open={showNutritionOnboarding}
          onClose={() => setShowNutritionOnboarding(false)}
          token={token}
          onComplete={() => {
            setShowNutritionOnboarding(false);
            // Force reload of nutrition data
            window.location.reload();
          }}
        />
      </PortalSettingsContext.Provider>
    </CustomerAuthContext.Provider>
  );
};

// =============================================
// GUEST VIEW (Not logged in) - Dark Theme
// =============================================

const GuestView = ({ activeTab, setActiveTab, isEmbed, darkTheme }) => {
  const { t } = useTranslation();
  // Einstiegs-Assistent: 'ask-existing' → 'ask-prescription' → 'auth'.
  // Bei Embed/Buchung oder Register direkt zur Auth.
  const [entryStep, setEntryStep] = useState(isEmbed ? 'auth' : 'ask-existing');

  // Auto-redirect to login if not on a specific auth page
  useEffect(() => {
    if (activeTab === 'booking') {
      setActiveTab('login');
    }
  }, []);

  const chooseExisting = () => {
    localStorage.setItem('onboarding_is_new', 'no');
    localStorage.removeItem('onboarding_has_prescription');
    setEntryStep('auth');
  };
  const chooseNew = () => {
    localStorage.setItem('onboarding_is_new', 'yes');
    setEntryStep('ask-prescription');
  };
  const choosePrescription = (val) => {
    localStorage.setItem('onboarding_has_prescription', val ? 'yes' : 'no');
    setEntryStep('auth');
  };

  const QuestionCard = ({ children }) => (
    <div className="max-w-md mx-auto rounded-2xl border border-gray-700 bg-gray-900/70 backdrop-blur p-6 sm:p-8" data-testid="portal-entry-card">
      {children}
    </div>
  );
  const ChoiceButton = ({ icon: Icon, title, subtitle, onClick, testid, primary }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-4 rounded-xl border px-4 py-4 text-left transition-colors ${primary ? 'border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20' : 'border-gray-700 bg-gray-800/60 hover:bg-gray-800'}`}
    >
      <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${primary ? 'bg-amber-500 text-black' : 'bg-gray-700 text-amber-400'}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-white">{title}</span>
        {subtitle && <span className="block text-sm text-white/60">{subtitle}</span>}
      </span>
    </button>
  );

  const renderAuth = () => (
    activeTab === 'register' ? (
      <RegisterForm onBack={() => setActiveTab('login')} darkTheme={darkTheme} />
    ) : (
      <>
        {!isEmbed && (
          <button type="button" onClick={() => setEntryStep('ask-existing')}
            className="mb-3 inline-flex items-center gap-1 text-sm text-white/60 hover:text-white" data-testid="portal-entry-back-auth">
            <ArrowLeft className="h-4 w-4" /> {t('common.back', 'Zurück')}
          </button>
        )}
        <LoginForm
          onBack={() => setActiveTab('login')}
          onRegister={() => setActiveTab('register')}
          darkTheme={darkTheme}
        />
      </>
    )
  );

  return (
    <div className="space-y-6">
      {!isEmbed && (
        <div className="flex justify-end">
          <PortalLanguageControl />
        </div>
      )}
      {!isEmbed && <PublicInstallAppBanner />}

      {entryStep === 'ask-existing' && activeTab !== 'register' ? (
        <QuestionCard>
          <h2 className="text-xl font-bold text-white mb-1" data-testid="portal-entry-existing-title">
            {t('portal.entry.welcome', 'Willkommen')} 👋
          </h2>
          <p className="text-white/70 mb-6">{t('portal.entry.existing_q', 'Bist du schon Kunde bei uns?')}</p>
          <div className="space-y-3">
            <ChoiceButton icon={UserCheck} primary
              title={t('portal.entry.existing_yes', 'Ja, ich habe ein Konto')}
              subtitle={t('portal.entry.existing_yes_sub', 'Mit E-Mail-Code anmelden')}
              onClick={chooseExisting} testid="portal-entry-existing-yes" />
            <ChoiceButton icon={UserPlus}
              title={t('portal.entry.existing_no', 'Nein, ich bin neu hier')}
              subtitle={t('portal.entry.existing_no_sub', 'Konto in wenigen Schritten erstellen')}
              onClick={chooseNew} testid="portal-entry-existing-no" />
          </div>
        </QuestionCard>
      ) : entryStep === 'ask-prescription' && activeTab !== 'register' ? (
        <QuestionCard>
          <button type="button" onClick={() => setEntryStep('ask-existing')}
            className="mb-3 inline-flex items-center gap-1 text-sm text-white/60 hover:text-white" data-testid="portal-entry-back-prescription">
            <ArrowLeft className="h-4 w-4" /> {t('common.back', 'Zurück')}
          </button>
          <h2 className="text-xl font-bold text-white mb-1" data-testid="portal-entry-prescription-title">
            {t('portal.entry.rx_q', 'Hast du eine Verordnung?')}
          </h2>
          <p className="text-white/70 mb-6">{t('portal.entry.rx_sub', 'Ein ärztliches Rezept für Physiotherapie oder Training. Wir lesen es automatisch aus und füllen deine Daten aus.')}</p>
          <div className="space-y-3">
            <ChoiceButton icon={FileText} primary
              title={t('portal.entry.rx_yes', 'Ja, ich habe eine Verordnung')}
              subtitle={t('portal.entry.rx_yes_sub', 'Nach der Anmeldung kannst du sie hochladen')}
              onClick={() => choosePrescription(true)} testid="portal-entry-rx-yes" />
            <ChoiceButton icon={User}
              title={t('portal.entry.rx_no', 'Nein, keine Verordnung')}
              subtitle={t('portal.entry.rx_no_sub', 'Du gibst deine Daten selbst ein')}
              onClick={() => choosePrescription(false)} testid="portal-entry-rx-no" />
          </div>
        </QuestionCard>
      ) : (
        renderAuth()
      )}
    </div>
  );
};

// =============================================
// Welle A — Gift Points Card (Self-Service)
// =============================================
const GiftPointsCard = ({ token }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  return (
    <>
      {/* Kompakte Zeile statt grosser Karte — Farben als feste Hex-Werte,
          damit sie von keinem Theme/Override unlesbar gemacht werden können. */}
      <div className="border border-amber-500/40 rounded-xl px-3 py-2 flex items-center gap-3" style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.12), transparent)' }} data-testid="portal-gift-card">
        <span className="text-xl shrink-0" aria-hidden>🎁</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: '#fcd34d' }}>{t('portal.gift.title')}</p>
          <p className="text-xs leading-snug" style={{ color: '#e4e4e7' }}>
            {t('portal.gift.subtitle')}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setDialogOpen(true)}
          className="bg-amber-500 hover:bg-amber-600 font-semibold shrink-0"
          style={{ color: '#1c1917' }}
          data-testid="portal-gift-points-btn"
        >
          {t('portal.gift.button')}
        </Button>
      </div>
      <GiftPointsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        token={token}
      />
    </>
  );
};

// =============================================
// CUSTOMER DASHBOARD (Logged in) - Dark Theme
// =============================================

const CustomerDashboard = ({ activeTab, setActiveTab, isEmbed, isEmbedFullPortal, darkTheme, onOpenNutritionOnboarding, onStartTour, profileIncompleteRedirect, setProfileIncompleteRedirect, bookingPreselectServiceId }) => {
  const { t } = useTranslation();
  const { token, customer, refreshCustomer } = useCustomerAuth();
  const contentRef = React.useRef(null);
  
  // Check if customer has nutrition access
  const nutritionAccess = customer?.nutrition_access || {};
  const hasNutritionAccess = nutritionAccess.has_access === true;
  
  // Show navigation in full portal embed mode or when not embedded
  const showNavigation = !isEmbed || isEmbedFullPortal;
  
  // Auto-scroll to content when switching to nutrition tab (mobile)
  React.useEffect(() => {
    if (activeTab === 'nutrition' && contentRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [activeTab]);

  // Auto-refresh customer profile when user opens the locked nutrition tab.
  // Reason: Admin-side toggles (Studio → CustomerSettingsTab → Ernährungs-App
  // freigeschaltet) used to only become visible after a full re-login. By
  // re-fetching /portal/auth/me on tab-open we close that gap — the moment
  // the admin enables it, the customer sees the unlock card on next tab tap.
  // Same pattern protects against stale session for status changes, package
  // upgrades and any other server-side feature flag.
  React.useEffect(() => {
    if (activeTab === 'nutrition' && !hasNutritionAccess && refreshCustomer) {
      refreshCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Module-Gating: Tabs, die ein gebuchtes Modul voraussetzen, erscheinen nur,
  // wenn das Studio das Modul gebucht hat (GET /api/portal/modules). Kern-Tabs
  // (Dashboard, Termine, Dokumente, Belege, Profil) sind immer sichtbar.
  const [enabledModules, setEnabledModules] = React.useState(null); // null = lädt noch
  React.useEffect(() => {
    if (!token) return;
    let active = true;
    import('axios').then(({ default: ax }) => {
      ax.get(`${API}/portal/modules`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { if (active) setEnabledModules(Array.isArray(r?.data?.enabled) ? r.data.enabled : []); })
        .catch(() => { if (active) setEnabledModules('__ALL__'); }); // fail-open bei Fehler
    });
    return () => { active = false; };
  }, [token]);

  const moduleEnabled = React.useCallback((keys) => {
    if (enabledModules === '__ALL__') return true;   // Fehler → alles zeigen (abwärtskompatibel)
    if (enabledModules === null) return false;       // lädt → modulabhängige Tabs noch ausblenden
    return keys.some((k) => enabledModules.includes(k));
  }, [enabledModules]);

  // Group-Fitness tab — nur sichtbar, wenn ein bepreister Kurs + kommender Termin existiert
  const [groupFitnessAvailable, setGroupFitnessAvailable] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    if (!token) return;
    import('axios').then(({ default: ax }) => {
      ax.get(`${API}/courses/offering-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { if (active) setGroupFitnessAvailable(r?.data?.group_fitness_available === true); })
        .catch(() => { if (active) setGroupFitnessAvailable(false); });
    });
    return () => { active = false; };
  }, [token]);

  // Tab-Definitionen in Anzeige-Reihenfolge. `requires` = Modul-Keys (ODER-
  // Verknüpfung); fehlt es, ist der Tab ein Kern-Tab und immer sichtbar.
  // `gfGate` = über den Kurs-Angebotsstatus gesteuert.
  const tabDefs = [
    { id: 'dashboard', label: t('portal.nav.home'), shortLabel: t('portal.nav.home'), icon: Home },
    { id: 'booking', label: t('portal.nav.booking'), shortLabel: t('portal.nav.booking'), icon: Calendar, requires: ['booking'] },
    { id: 'appointments', label: t('portal.nav.appointments'), shortLabel: t('portal.nav.appointments'), icon: Clock },
    { id: 'packages', label: t('portal.nav.packages'), shortLabel: t('portal.nav.packages'), icon: Package, requires: ['subscriptions', 'points', 'shop'] },
    { id: 'fitness', label: t('portal.nav.fitness'), shortLabel: t('portal.nav.fitness'), icon: Activity, requires: ['training'] },
    { id: 'nutrition', label: t('portal.nav.nutrition'), shortLabel: t('portal.nav.nutrition_short'), icon: Apple, requires: ['nutrition'] },
    { id: 'group-fitness', label: 'Group Fitness', shortLabel: 'Kurse', icon: Dumbbell, gfGate: true },
    { id: 'documents', label: t('portal.nav.documents'), shortLabel: t('portal.nav.documents'), icon: FileText },
    { id: 'insurance-receipts', label: t('portal.nav.receipts'), shortLabel: t('portal.nav.receipts'), icon: ClipboardList },
    { id: 'profile', label: t('portal.nav.profile'), shortLabel: t('portal.nav.profile'), icon: User },
  ];

  const tabs = tabDefs.filter((tab) => {
    if (tab.gfGate) return groupFitnessAvailable;      // Group-Fitness: Angebots-Status
    if (tab.requires) return moduleEnabled(tab.requires);
    return true;                                       // Kern-Tab
  });
  
  return (
    <div className="space-y-4">
      {/* 08/2026: Einmalige Zahlungswahl (Selbstzahler vs. Auto-Belastung) */}
      <PaymentChoicePrompt />
      {showNavigation && (
        <div
          className="animate-fade-up"
          style={{
            borderBottom: `1px solid ${darkTheme.border}`,
            paddingBottom: '6px',
          }}
        >
          <div className="flex gap-0.5 overflow-x-auto scrollbar-hide justify-start lg:justify-center" style={{ WebkitOverflowScrolling: 'touch' }}>
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              const showDivider = tab.id === 'fitness' || tab.id === 'documents';
              return (
                <React.Fragment key={tab.id}>
                  {showDivider && (
                    <div className="w-px self-stretch my-1.5 mx-1.5 flex-shrink-0" style={{ backgroundColor: darkTheme.border }} />
                  )}
                  <button
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium transition-colors duration-200 flex-shrink-0 ${isActive ? '' : 'hover:bg-white/[0.04]'}`}
                    style={isActive ? {
                      backgroundColor: darkTheme.cardBg,
                      color: darkTheme.accent,
                    } : {
                      backgroundColor: 'transparent',
                      color: darkTheme.textMuted,
                    }}
                    onClick={() => setActiveTab(tab.id)}
                    title={tab.label}
                    data-testid={`portal-nav-${tab.id}-button`}
                  >
                    <Icon size={15} strokeWidth={1.75} style={{ color: isActive ? darkTheme.accent : 'currentColor' }} />
                    <span className="hidden md:inline text-xs">{tab.shortLabel}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab Content - Dark theme container with fade animation */}
      <div ref={contentRef} className="rounded-xl overflow-hidden">
        <div className="portal-content animate-fade-up" key={activeTab}>
          {activeTab === 'dashboard' && (
            <PortalDashboard darkTheme={darkTheme} customer={customer} setActiveTab={setActiveTab} />
          )}
          {activeTab === 'booking' && (
            <Suspense fallback={<PortalTabLoader label={t('portal.loader.calendar')} testId="portal-tab-loader-booking" />}>
              <PrescriptionStatusBanner />
              <BookingCalendar preselectServiceId={bookingPreselectServiceId} onNavigateToProfile={() => { if (setProfileIncompleteRedirect) setProfileIncompleteRedirect(true); setActiveTab('profile'); }} />
            </Suspense>
          )}
          {activeTab === 'appointments' && (
            <Suspense fallback={<PortalTabLoader label={t('portal.loader.appointments')} testId="portal-tab-loader-appointments" />}>
              <MyAppointments />
            </Suspense>
          )}
          {activeTab === 'packages' && (
            <div className="space-y-4">
              {/* Open Items - shows only if there are open items */}
              <OpenItems />
              {/* Iter 299 (Welle A) — Punkte verschenken */}
              <GiftPointsCard token={token} />
              {/* Freundeskreis-Preis (Anzeige) */}
              <Suspense fallback={null}>
                <FriendshipTreeCard token={token} />
              </Suspense>
              {/* Main packages view */}
              <Suspense fallback={<PortalTabLoader label={t('portal.loader.packages')} testId="portal-tab-loader-packages" />}>
                <MyPackages />
              </Suspense>
            </div>
          )}
          {activeTab === 'fitness' && (
            <Suspense fallback={<PortalTabLoader label={t('portal.loader.fitness')} testId="portal-tab-loader-fitness" />}>
              <CombinedFitnessHub customerId={customer?.id} token={token} />
            </Suspense>
          )}
          {activeTab === 'group-fitness' && (
            <Suspense fallback={<PortalTabLoader label="Kurse werden geladen…" testId="portal-tab-loader-group-fitness" />}>
              <GroupFitnessTab customerId={customer?.id} token={token} darkTheme={darkTheme} />
            </Suspense>
          )}
          {activeTab === 'nutrition' && (
            hasNutritionAccess ? (
              <Suspense fallback={<PortalTabLoader label={t('portal.loader.nutrition')} testId="portal-tab-loader-nutrition" />}>
                {nutritionAccess?.reason === 'trial' && (
                  <div
                    className="mb-4 overflow-hidden rounded-2xl relative"
                    data-testid="portal-nutrition-trial-banner"
                    style={{
                      background: 'linear-gradient(135deg, rgba(201,162,39,0.22), rgba(201,162,39,0.08))',
                      border: '1px solid rgba(201,162,39,0.45)',
                    }}
                  >
                    {/* Progress fill showing days consumed — drains left to right */}
                    <div
                      className="absolute inset-y-0 left-0 transition-all"
                      style={{
                        width: `${100 - ((nutritionAccess.trial_days_remaining / (nutritionAccess.trial_total_days || 7)) * 100)}%`,
                        background: 'rgba(201,162,39,0.08)',
                      }}
                    />
                    <div className="relative flex items-center gap-3 p-4">
                      <div className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
                           style={{ backgroundColor: 'rgba(201,162,39,0.25)' }}>
                        <span className="text-xl">⏱️</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold" style={{ color: '#c9a34e' }}>
                          {t('portal.trial.active', { days: nutritionAccess.trial_days_remaining, unit: nutritionAccess.trial_days_remaining === 1 ? t('portal.trial.day') : t('portal.trial.days') })}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: darkTheme.textMuted }}>
                          {t('portal.trial.unlock_hint')}
                        </p>
                      </div>
                      <div
                        className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold"
                        style={{
                          backgroundColor: '#c9a34e',
                          color: '#000',
                        }}
                      >
                        {nutritionAccess.trial_days_remaining}/{nutritionAccess.trial_total_days || 7}
                      </div>
                    </div>
                  </div>
                )}
                <NutritionHub
                  token={token}
                  darkTheme={darkTheme}
                  primaryColor={darkTheme.accent}
                  onOpenOnboarding={onOpenNutritionOnboarding}
                />
              </Suspense>
            ) : (
              <div 
                className="p-6 rounded-xl text-center"
                style={{ backgroundColor: darkTheme.cardBg }}
                data-testid="portal-nutrition-locked-card"
              >
                <Apple size={48} className="mx-auto mb-4" style={{ color: darkTheme.textMuted }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: darkTheme.text }} data-testid="portal-nutrition-locked-title">
                  Ernährungs-App noch nicht freigeschaltet
                </h3>
                <p className="text-sm mb-4" style={{ color: darkTheme.textMuted }} data-testid="portal-nutrition-locked-message">
                  {nutritionAccess.message || 'Noch 10 Trainings bis zur Freischaltung'}
                </p>
                {nutritionAccess.total_completed !== undefined && (
                  <div className="mt-4" data-testid="portal-nutrition-locked-progress">
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                      <div 
                        className="h-2 rounded-full transition-all"
                        style={{ 
                          width: `${Math.min(100, (nutritionAccess.total_completed / 10) * 100)}%`,
                          backgroundColor: darkTheme.accent 
                        }}
                      />
                    </div>
                    <p className="text-xs" style={{ color: darkTheme.textMuted }}>
                      {nutritionAccess.total_completed}/10 Trainings absolviert
                    </p>
                  </div>
                )}
                {/* Manueller Refresh — falls Admin gerade freigeschaltet hat
                    und Auto-Refresh den neuen Status noch nicht zeigt. */}
                <button
                  type="button"
                  onClick={() => refreshCustomer && refreshCustomer()}
                  className="mt-5 text-xs underline opacity-70 hover:opacity-100"
                  style={{ color: darkTheme.textMuted }}
                  data-testid="portal-nutrition-locked-refresh-btn"
                >
                  Status aktualisieren
                </button>
              </div>
            )
          )}
          {activeTab === 'documents' && (
            <Suspense fallback={<PortalTabLoader label={t('portal.loader.documents')} testId="portal-tab-loader-documents" />}>
              <CustomerInsuranceDocuments 
                customerId={customer?.id}
                primaryColor={darkTheme.accent}
                darkTheme={darkTheme}
              />
            </Suspense>
          )}
          {activeTab === 'insurance-receipts' && (
            <Suspense fallback={<PortalTabLoader label={t('portal.loader.receipts')} testId="portal-tab-loader-receipts" />}>
              <div className="space-y-4">
                <PortalInvoices token={token} darkTheme={darkTheme} />
                <PortalInsuranceReceipts token={token} customerId={customer?.id} darkTheme={darkTheme} />
              </div>
            </Suspense>
          )}
          {activeTab === 'profile' && (
            <Suspense fallback={<PortalTabLoader label={t('portal.loader.profile')} testId="portal-tab-loader-profile" />}>
              <div className="space-y-4">
                <CustomerProfile onStartTour={onStartTour} autoOpenProfile={profileIncompleteRedirect} onProfileOpened={() => setProfileIncompleteRedirect(false)} />
                <ConsentForms token={token} />
              </div>
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;
